import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Star, Home as HomeIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import api, { resolveImage, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite, STATUS } from "@/context/SiteContext";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { refreshProperties } = useSite();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ whatsapp_number: "", instagram_url: "", facebook_url: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: props }, { data: st }] = await Promise.all([api.get("/admin/properties"), api.get("/settings")]);
      setItems(props);
      setSettings(st);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === null) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  const counts = {
    total: items.length,
    disponivel: items.filter((p) => p.status === "disponivel").length,
    reservado: items.filter((p) => p.status === "reservado").length,
    vendido: items.filter((p) => p.status === "vendido").length,
    destaque: items.filter((p) => p.featured).length,
  };

  const updateField = async (prop, patch) => {
    const payload = {
      title: prop.title, type: prop.type, city: prop.city, neighborhood: prop.neighborhood,
      price: prop.price, area_privativa: prop.area_privativa, area_total: prop.area_total,
      bedrooms: prop.bedrooms, suites: prop.suites, bathrooms: prop.bathrooms, parking: prop.parking,
      description: prop.description, features: prop.features, status: prop.status,
      featured: prop.featured, published: prop.published, map_url: prop.map_url, ...patch,
    };
    try {
      const { data } = await api.put(`/admin/properties/${prop.id}`, payload);
      setItems((prev) => prev.map((i) => (i.id === prop.id ? data : i)));
      refreshProperties();
      toast.success("Imóvel atualizado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (prop) => {
    if (!window.confirm(`Excluir "${prop.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/properties/${prop.id}`);
      setItems((prev) => prev.filter((i) => i.id !== prop.id));
      refreshProperties();
      toast.success("Imóvel excluído.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Configurações salvas.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const inputCls =
    "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition bg-white";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Seo title="Painel Administrativo | Casa Vale" description="Gestão de imóveis Casa Vale." />
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Ir para o site"><Logo light /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-slate-300 hover:text-white transition-colors hidden sm:block" data-testid="admin-view-site">
              Ver site
            </Link>
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">Gestão de Imóveis</h1>
            <p className="text-sm text-slate-500 mt-1">Cadastre, edite e gerencie os anúncios do site.</p>
          </div>
          <Link
            to="/admin/imoveis/novo"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-bold px-6 py-3 rounded-full transition-colors"
            data-testid="new-property-btn"
          >
            <Plus className="w-4 h-4" /> Novo Imóvel
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="metrics-cards">
          {[
            { label: "Total", value: counts.total },
            { label: "Disponíveis", value: counts.disponivel },
            { label: "Reservados", value: counts.reservado },
            { label: "Vendidos", value: counts.vendido },
            { label: "Em destaque", value: counts.destaque },
          ].map((m) => (
            <div key={m.label} className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
              <p className="font-mono-num text-2xl font-semibold text-slate-900">{m.value}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="properties-table-wrap">
          {loading ? (
            <div className="p-10 text-center text-slate-400 text-sm">Carregando imóveis...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-empty-state">
              <HomeIcon className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Nenhum imóvel cadastrado. Clique em "Novo Imóvel" para começar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="properties-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-5 py-4">Imóvel</th>
                    <th className="px-5 py-4">Tipo</th>
                    <th className="px-5 py-4">Preço</th>
                    <th className="px-5 py-4">Situação</th>
                    <th className="px-5 py-4 text-center">Destaque</th>
                    <th className="px-5 py-4 text-center">Publicado</th>
                    <th className="px-5 py-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-row-${p.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {p.images?.[0] ? (
                            <img src={resolveImage(p.images[0])} alt="" className="w-14 h-11 rounded-lg object-cover bg-slate-100" />
                          ) : (
                            <div className="w-14 h-11 rounded-lg bg-slate-100" />
                          )}
                          <div>
                            <p className="font-bold text-slate-800 leading-tight">{p.title}</p>
                            <p className="text-xs text-slate-400">{[p.neighborhood, p.city].filter(Boolean).join(" · ")}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 capitalize text-slate-600">{p.type}</td>
                      <td className="px-5 py-3 font-mono-num text-slate-700">
                        {p.price != null ? `R$ ${Number(p.price).toLocaleString("pt-BR")}` : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={p.status}
                          onChange={(e) => updateField(p, { status: e.target.value })}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold bg-white"
                          aria-label={`Situação de ${p.title}`}
                          data-testid={`status-select-${p.id}`}
                        >
                          {Object.entries(STATUS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => updateField(p, { featured: !p.featured })}
                          className={`p-2 rounded-full transition-colors ${p.featured ? "text-amber-500 bg-amber-50" : "text-slate-300 hover:text-amber-400"}`}
                          aria-label={p.featured ? "Remover destaque" : "Destacar na página inicial"}
                          data-testid={`featured-toggle-${p.id}`}
                        >
                          <Star className="w-4 h-4" fill={p.featured ? "currentColor" : "none"} />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => updateField(p, { published: !p.published })}
                          className={`p-2 rounded-full transition-colors ${p.published ? "text-brand bg-brand-light" : "text-slate-300 hover:text-slate-500"}`}
                          aria-label={p.published ? "Despublicar anúncio" : "Publicar anúncio"}
                          data-testid={`publish-toggle-${p.id}`}
                        >
                          {p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/admin/imoveis/${p.id}/editar`}
                            className="p-2 rounded-full text-slate-400 hover:text-brand hover:bg-brand-light transition-colors"
                            aria-label={`Editar ${p.title}`}
                            data-testid={`edit-btn-${p.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => remove(p)}
                            className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            aria-label={`Excluir ${p.title}`}
                            data-testid={`delete-btn-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="settings-panel">
          <h2 className="text-lg font-bold text-slate-900">Configurações de Contato</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            O número de WhatsApp cadastrado aqui é usado em todos os botões de contato do site.
          </p>
          <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cfg-whatsapp" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                WhatsApp (com DDI e DDD)
              </label>
              <input
                id="cfg-whatsapp"
                type="text"
                value={settings.whatsapp_number}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="Ex.: 5512999998888"
                className={inputCls}
                data-testid="settings-whatsapp-input"
              />
            </div>
            <div>
              <label htmlFor="cfg-instagram" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Instagram (URL)
              </label>
              <input
                id="cfg-instagram"
                type="url"
                value={settings.instagram_url}
                onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                placeholder="https://instagram.com/..."
                className={inputCls}
                data-testid="settings-instagram-input"
              />
            </div>
            <div>
              <label htmlFor="cfg-facebook" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Facebook (URL)
              </label>
              <input
                id="cfg-facebook"
                type="url"
                value={settings.facebook_url}
                onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                placeholder="https://facebook.com/..."
                className={inputCls}
                data-testid="settings-facebook-input"
              />
            </div>
            <div className="md:col-span-3">
              <button
                type="submit"
                disabled={savingSettings}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-bold px-7 py-3 rounded-full transition-colors"
                data-testid="settings-save-btn"
              >
                {savingSettings ? "Salvando..." : "Salvar Configurações"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
